// Canonical apparel size sort for the Warehouse Portal variant rows.
// Groups variants by Youth -> Womens -> Adult, then within group by canonical
// size order (XXS/XS/S/M/L/XL/XXL/XXXL/4XL/5XL with synonyms 2XS<->XXS,
// 2XL<->XXL, 3XL<->XXXL), then by secondary-axis (color etc.) alphabetically.
// Pure / dependency-free; covered by src/__tests__/warehouseSortVariants.test.js.

const CANONICAL_SIZES = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL', '4XL', '5XL'];
const SIZE_INDEX = Object.fromEntries(CANONICAL_SIZES.map((s, i) => [s, i]));
SIZE_INDEX['2XS'] = SIZE_INDEX['XXS'];   // = 0
SIZE_INDEX['2XL'] = SIZE_INDEX['XXL'];   // = 6
SIZE_INDEX['3XL'] = SIZE_INDEX['XXXL'];  // = 7

// Word-form -> canonical-letter aliases (handles "Small" / "Adult Small" etc.).
const SIZE_WORD_ALIASES = {
    'SMALL':    'S',
    'MEDIUM':   'M',
    'LARGE':    'L',
    'XSMALL':   'XS',
    'XLARGE':   'XL',
    'XXSMALL':  'XXS',
    'XXLARGE':  'XXL',
    'XXXLARGE': 'XXXL',
};

const ONE_SIZE_IDX = 50;
const UNKNOWN_IDX  = 99;

const GROUP_YOUTH   = 0;
const GROUP_WOMENS  = 1;
const GROUP_ADULT   = 2;
const GROUP_UNKNOWN = 3;

// Detect 'Youth' / 'Y' prefix in many shapes: "Youth Small", "Y-S", "YS".
const YOUTH_WORD_RE      = /\b(youth)\b/i;
const YOUTH_PREFIX_RE    = /^y[-\s]/i;                          // "Y-S", "Y S"
const YOUTH_COMPACT_RE   = /^y(xx?s|x?s|m|x?l|xx?l|xxxl)$/i;    // "YS", "YXS", "YM"
const WOMENS_WORD_RE     = /\b(women|womens|ladies)\b/i;

export function parseSizeKey(raw) {
    if (raw == null) return { group: GROUP_UNKNOWN, sizeIdx: UNKNOWN_IDX };
    const s = String(raw).trim();
    const sLow = s.toLowerCase();

    let group = GROUP_ADULT;
    if (YOUTH_WORD_RE.test(sLow) || YOUTH_PREFIX_RE.test(sLow) || YOUTH_COMPACT_RE.test(sLow)) {
        group = GROUP_YOUTH;
    } else if (WOMENS_WORD_RE.test(sLow)) {
        group = GROUP_WOMENS;
    }

    // Isolate the size token by stripping the group prefix (word or letter).
    // Examples: "Youth Small" -> "Small", "Y-S" -> "S", "YS" -> "S",
    //           "Adult Small" -> "Small", "Womens XL" -> "XL".
    let sizeToken = s
        .replace(/^(youth|adult|women(?:s)?|ladies)[-\s]+/i, '')
        .replace(/^y[-\s]+/i, '')        // "Y-S", "Y S"
        .replace(/^y(?=(xx?s|x?s|m|x?l|xx?l|xxxl)$)/i, '')  // "YS", "YXL" -> "S", "XL"
        .replace(/^w[-\s]+/i, '')        // "W-M"
        .trim()
        .toUpperCase();
    if (!sizeToken) sizeToken = s.trim().toUpperCase();

    // Word -> canonical letter alias ("SMALL" -> "S").
    if (sizeToken in SIZE_WORD_ALIASES) {
        sizeToken = SIZE_WORD_ALIASES[sizeToken];
    }

    if (/^(ONE\s*SIZE|OS|O\/S)$/i.test(sizeToken)) {
        return { group, sizeIdx: ONE_SIZE_IDX };
    }
    if (sizeToken in SIZE_INDEX) {
        return { group, sizeIdx: SIZE_INDEX[sizeToken] };
    }
    return { group, sizeIdx: UNKNOWN_IDX };
}

function detectSizeAxis(variants) {
    for (const v of (variants || [])) {
        for (const opt of (v.selectedOptions || [])) {
            if ((opt.name || '').toLowerCase() === 'size') return opt.name;
        }
    }
    return null;
}

export function sortVariants(variants) {
    if (!Array.isArray(variants) || variants.length === 0) return variants || [];
    const axis = detectSizeAxis(variants);
    if (!axis) {
        return [...variants].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
    }
    const keyed = variants.map(v => {
        const sizeOpt = (v.selectedOptions || []).find(o => o.name === axis);
        const sk      = parseSizeKey(sizeOpt?.value);
        const other   = (v.selectedOptions || [])
            .filter(o => o.name !== axis)
            .map(o => `${o.name}:${o.value}`.toLowerCase())
            .sort()
            .join('|');
        return { v, group: sk.group, sizeIdx: sk.sizeIdx, otherAlpha: other };
    });
    keyed.sort((a, b) => {
        if (a.group !== b.group) return a.group - b.group;
        if (a.sizeIdx !== b.sizeIdx) return a.sizeIdx - b.sizeIdx;
        return a.otherAlpha.localeCompare(b.otherAlpha);
    });
    return keyed.map(k => k.v);
}
